/**
 * The trainee's eating log, as the trainer reads it.
 *
 * Two endpoints, deliberately: a month of per-day totals for the calendar (`getMonthSummary`, one
 * indexed query over the day log's promoted columns), and the one open day in full (`getDayLog`).
 * Drawing the month out of the day endpoint would be thirty calls carrying payload the grid
 * discards.
 *
 * ── this file used to say the opposite, and it is worth knowing why ──
 *
 * It previously served the V1-shaped weekly log and carried a long note explaining that swapped and
 * trainee-added foods, the alternative meal chosen, water and per-supplement ticks "have no source
 * at all": in V1 the plan and the actual were the SAME rows — the day was materialised from the menu
 * and then edited in place — so none of it could be reconstructed. That note ended by predicting
 * that all of it returns with the V2 daily log, "which stores the two separately".
 *
 * It does, and this is that. `nutrition_v2_day_log` holds `plan_json` (the menu frozen as it was
 * that day) beside `log_json` (the trainee's diff against it), and `getDayLog` hands both over
 * unresolved. Everything the old note listed as impossible is now simply a field.
 *
 * The weekly endpoint itself still exists server-side and is still used by `ClientWeightTrackPage`
 * through `LogFoodService` — a different module. Nothing here calls it any more.
 */

import {
    adminNutritionDayLogV2ControllerDay,
    adminTraineeFoodLogGetTraineeFoodLogSummary,
} from "../api/generated";
import type {
    CoachDaySummaryV2Dto,
    MacroSetDto,
    DayLogV2Dto,
} from "../api/generated/types.gen";
import type { MacroSet, MenuV2 } from "../componenets/nutrition/v2/model/nutritionV2Model";
import { createDayLog, type DayLogV2 } from "../componenets/nutrition/v2/model/dayLogV2Model";

const KEY = "YYYY-MM-DD";

/* ── a month at a time, for the calendar ──────────────────────────────────────────────────── */

/**
 * One day as the month grid reads it: what was eaten, what it was aiming at, and whether the
 * trainee said anything at all.
 *
 * `targets` is absent for a day that was never planned — which is NOT a target of zero. A day with
 * no goal cannot be on target or off it, and the grid leaves such a cell uncoloured.
 */
export type DaySummaryV2 = {
    /** YYYY-MM-DD */
    date: string;
    /** the trainee reported this day — the same definition `DayLogV2.logged` carries */
    reported: boolean;
    /** fiber included — straight off the day row's `actual_fiber` column */
    totals: MacroSet;
    targets?: MacroSet;
};

const macroSet = (m: MacroSetDto | undefined): MacroSet => ({
    calories: m?.calories ?? 0,
    protein: m?.protein ?? 0,
    carbs: m?.carbs ?? 0,
    fats: m?.fats ?? 0,
    fiber: m?.fiber ?? 0,
});

const hasTarget = (m: MacroSetDto | undefined): boolean =>
    Boolean(m && (m.calories || m.protein || m.carbs || m.fats));

export const toDaySummaries = (rows: CoachDaySummaryV2Dto[]): DaySummaryV2[] =>
    rows
        .filter((row) => Boolean(row.date))
        .map((row) => ({
            date: row.date as string,
            reported: Boolean(row.reported),
            totals: macroSet(row.totals),
            targets: hasTarget(row.targets) ? macroSet(row.targets) : undefined,
        }));

/**
 * A range of days, totals and targets only — no meals, no foods.
 *
 * A different endpoint from `getWeekLog` rather than five calls to it. The weekly log returns every
 * food of every day and costs the server seven round-trips per week, so drawing a month out of it
 * would be five or six such calls carrying a payload the grid discards. This one is a single indexed
 * query over the day log's promoted columns.
 *
 * Days the trainee never touched are simply absent — the grid renders a missing day and an
 * unreported one identically, because they mean the same thing to a trainer.
 */
export const getMonthSummary = async (
    traineeId: string,
    from: string,
    to: string
): Promise<DaySummaryV2[]> => {
    const result = await adminTraineeFoodLogGetTraineeFoodLogSummary({
        path: { traineeId: Number(traineeId) },
        query: { from, to },
    });
    if (result.error) {
        throw result.error;
    }
    return toDaySummaries(result.data || []);
};

/* ── one day, plan and log ────────────────────────────────────────────────────────────────── */

/**
 * One day as the trainer opens it: the menu the trainee was given, and what they did to it.
 *
 * The pair, deliberately unresolved. Everything about which food was ticked, swapped or added lives
 * in `log` as uids pointing into `plan`, and `dayLogV2Model` is what turns the two into rows — the
 * same resolution the trainee's own app performs, ported rather than reinvented so the coach and the
 * trainee cannot be shown different food for the same day.
 */
export type TraineeDayV2 = {
    /** YYYY-MM-DD */
    date: string;
    /** The menu this day is read through. Null only when the trainee has no menu at all. */
    plan: MenuV2 | null;
    log: DayLogV2;
    /**
     * Whether `plan` is a snapshot of THIS date or merely the menu as it stands now.
     *
     * False means nothing was ever written for the date, so the menu had to be resolved live — and if
     * the trainer has changed it since, the date is right and the food is not. Any surface showing
     * such a day has to say so; see `DayPlanView`.
     */
    planFrozen: boolean;
    /** The trainee actually said something. NOT the same as a row existing. */
    reported: boolean;
    /** What was reported, off the promoted columns — the same numbers the month grid draws. */
    totals: MacroSet;
};

/**
 * Flatten the DTO's optional maps into total ones.
 *
 * Done here, once, rather than in the resolver: the generated `DayLogV2Dto` makes every field and
 * every map optional, and the resolver indexes them directly. Threading `?? {}` through forty call
 * sites to satisfy a type that is never actually partial on the wire is the worse trade.
 */
const toDayLog = (dto: DayLogV2Dto | undefined, date: string): DayLogV2 => ({
    ...createDayLog(date),
    chosenMenuId: dto?.chosenMenuId ?? null,
    chosenAlternativeByMeal: dto?.chosenAlternativeByMeal ?? {},
    chosenSubstitutionByFood: dto?.chosenSubstitutionByFood ?? {},
    chosenServingByFood: dto?.chosenServingByFood ?? {},
    replacedFoodByUid: (dto?.replacedFoodByUid ?? {}) as DayLogV2["replacedFoodByUid"],
    eaten: dto?.eaten ?? {},
    actualQuantity: dto?.actualQuantity ?? {},
    extraFoods: (dto?.extraFoods ?? {}) as DayLogV2["extraFoods"],
    waterMl: dto?.waterMl ?? 0,
    supplementsTaken: dto?.supplementsTaken ?? {},
});

export const getDayLog = async (traineeId: string, date: string): Promise<TraineeDayV2> => {
    const result = await adminNutritionDayLogV2ControllerDay({
        path: { traineeId: Number(traineeId), date },
    });
    if (result.error) {
        throw result.error;
    }
    // This endpoint IS wrapped in HttpResponse, unlike `getWeekLog` above — the two admin food-log
    // controllers differ on that, and the generated types confirm it either way.
    const body = result.data?.body;
    if (!body) {
        throw new Error("getDayLog: empty response body");
    }
    return {
        date: body.date ?? date,
        // The cast is the convention `NutritionV2Service.toMenu` already sets: the server returns
        // exactly this shape, and a hand mapper would silently drop whatever the server adds next.
        // Here it also covers a real gap — the generated `SubGroupDto` is missing `items`, which the
        // server does send and which every "במקום X" row is built from. See the note in the PR.
        plan: (body.plan as unknown as MenuV2) ?? null,
        log: toDayLog(body.log, body.date ?? date),
        planFrozen: body.planFrozen ?? false,
        reported: body.reported ?? false,
        totals: macroSet(body.totals),
    };
};
